import { faCheck, faCheckDouble, faSquare } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import EbdaEduNavbar from "components/navbars/EbdaEduNavbar";
import { useLanguage } from "context/LanguageContext";
import { useTheme } from "context/ThemeContext";
import { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { getPagesData, getUserRolesPermissions, giveRolePermissions } from "services/admins";

const WabysAdminUserRole = () => {
    const { language } = useLanguage();
    const { darkMode } = useTheme();

    const [roles, setRoles] = useState([]);
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);

    const [selectedRoleId, setSelectedRoleId] = useState("");
    const [selectedPageIds, setSelectedPageIds] = useState([]);

    useEffect(() => {
        const loadData = async () => {
            try {
                const [rolesResponse, pagesResponse] = await Promise.all([
                    getUserRolesPermissions(),
                    getPagesData()
                ]);

                setRoles(rolesResponse || []);
                setPages(pagesResponse || []);
            } catch (err) {
                toast.error(language ? "Failed to load roles/pages" : "فشل تحميل الأدوار والصفحات");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [language]);

    const selectedRole = useMemo(
        () => roles.find((role) => String(role.id) === String(selectedRoleId)) || null,
        [roles, selectedRoleId]
    );

    const handleSelectRole = (roleId) => {
        setSelectedRoleId(roleId);

        const role = roles.find((r) => String(r.id) === String(roleId));
        const connectedPageIds = role?.permissions?.map((p) => p.page_id) || [];
        setSelectedPageIds([...new Set(connectedPageIds)]);
    };

    const togglePage = (pageId) => {
        setSelectedPageIds((prev) =>
            prev.includes(pageId) ? prev.filter((id) => id !== pageId) : [...prev, pageId]
        );
    };

    const selectAllPages = () => {
        setSelectedPageIds(pages.map((p) => p.id));
    };

    const deselectAllPages = () => {
        setSelectedPageIds([]);
    };

    const handleSave = async () => {
        if (!selectedRoleId) {
            toast.error(language ? "Select a role first" : "اختر دور اولاً");
            return;
        }

        const payload = {
            user_role_id: Number(selectedRoleId),
            page_ids: selectedPageIds
        };
        try {
            await giveRolePermissions(payload)
            toast.success(language ? "done" : "تم");
        } catch (error) {
            toast.error(language ? "something went wrong please try again later" : "حصل خطا الرجاء المحاولة مرة اخري");
        }
    };

    return (
        <div className={`w-full min-h-screen flex flex-col items-center ${darkMode && "bg-watomsBlue text-white"}`}>
            <EbdaEduNavbar />

            <div className="w-[90%] mb-10 space-y-6" dir={language ? "ltr" : "rtl"}>
                <h1 className="text-xl font-bold mt-4">
                    {language ? "User Role Config" : "ادارة دور المستخدم"}
                </h1>

                <div
                    className={`rounded-2xl border shadow-sm p-4 space-y-3 ${darkMode ? "bg-white text-black border-black/10" : "bg-white border-slate-200"
                        }`}
                >
                    <label className="block text-sm font-semibold text-slate-600">
                        {language ? "Select Role" : "اختر الدور"}
                    </label>
                    <select
                        className="w-full rounded-xl px-3 py-2 border border-slate-200 outline-none"
                        value={selectedRoleId}
                        onChange={(e) => handleSelectRole(e.target.value)}
                        disabled={loading}
                    >
                        <option value="">{language ? "Select a role" : "اختر دور"}</option>
                        {roles.map((role) => (
                            <option key={role.id} value={role.id}>
                                {role.title}
                            </option>
                        ))}
                    </select>
                </div>

                {selectedRole && (
                    <div
                        className={`rounded-2xl border shadow-sm p-4 space-y-4 ${darkMode ? "bg-white text-black border-black/10" : "bg-white border-slate-200"
                            }`}
                    >
                        <div className="flex items-center justify-between flex-wrap gap-2">
                            <h2 className="font-bold">
                                {language ? "Pages" : "الصفحات"}{" "}
                                <span className="text-sm font-normal text-slate-500">
                                    ({selectedPageIds.length}/{pages.length})
                                </span>
                            </h2>
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={selectAllPages}
                                    className="flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-semibold border border-blue-600 text-blue-600 hover:bg-blue-50"
                                >
                                    <FontAwesomeIcon icon={faCheckDouble} />
                                    {language ? "Select All" : "اختيار الكل"}
                                </button>
                                <button
                                    type="button"
                                    onClick={deselectAllPages}
                                    className="flex items-center gap-2 rounded-xl px-3 py-1.5 text-sm font-semibold border border-slate-300 text-slate-600 hover:bg-slate-50"
                                >
                                    <FontAwesomeIcon icon={faSquare} />
                                    {language ? "Deselect All" : "إلغاء الكل"}
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {pages.map((page) => {
                                const checked = selectedPageIds.includes(page.id);
                                return (
                                    <button
                                        type="button"
                                        key={page.id}
                                        onClick={() => togglePage(page.id)}
                                        className={`flex items-center gap-3 rounded-xl px-3 py-2 border text-start transition-colors ${checked
                                            ? "bg-blue-50 border-blue-500 text-blue-700"
                                            : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                                            }`}
                                    >
                                        <span
                                            className={`w-5 h-5 flex items-center justify-center rounded border flex-shrink-0 ${checked ? "bg-blue-600 border-blue-600 text-white" : "border-slate-300"
                                                }`}
                                        >
                                            {checked && <FontAwesomeIcon icon={faCheck} className="text-xs" />}
                                        </span>
                                        <span className="text-sm font-medium truncate">{page.name}</span>
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            onClick={handleSave}
                            className="w-full rounded-xl py-2 font-semibold bg-blue-600 text-white hover:bg-blue-700"
                        >
                            {language ? "Save Changes" : "حفظ التغييرات"}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default WabysAdminUserRole;
